(() => {
  const galleryCache = new WeakMap();
  const touchStarts = new WeakMap();
  const mobileGalleryQuery = '(max-width: 989px), (hover: none), (pointer: coarse)';

  const getItems = (gallery) => {
    if (galleryCache.has(gallery)) return galleryCache.get(gallery);

    const template = gallery.querySelector('template[data-custom-bridal-gallery-items]');
    const items = template
      ? Array.from(template.content.querySelectorAll('[data-gallery-src]')).map((item) => ({
          src: item.dataset.gallerySrc,
          srcset: item.dataset.gallerySrcset,
          sizes: item.dataset.gallerySizes,
          alt: item.dataset.galleryAlt,
          width: item.dataset.galleryWidth,
          height: item.dataset.galleryHeight,
        }))
      : [];

    galleryCache.set(gallery, items);
    return items;
  };

  const updateIndicators = (gallery, index) => {
    const dots = gallery.querySelectorAll('[data-custom-bridal-gallery-index]');
    dots.forEach((dot) => {
      const isActive = Number.parseInt(dot.dataset.customBridalGalleryIndex, 10) === index;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', String(isActive));
    });

    const more = gallery.querySelector('[data-custom-bridal-gallery-more]');
    if (more) more.classList.toggle('is-active', index >= dots.length);
  };

  const setImageAttributes = (image, item) => {
    image.srcset = item.srcset;
    image.sizes = item.sizes;
    image.src = item.src;
    image.alt = item.alt;
    image.width = Number.parseInt(item.width, 10);
    image.height = Number.parseInt(item.height, 10);
  };

  const updateGalleryState = (gallery, index, itemCount) => {
    gallery.dataset.galleryIndex = String(index);

    const status = gallery.querySelector('[data-custom-bridal-gallery-status]');
    if (status) status.textContent = `Image ${index + 1} of ${itemCount}`;
    updateIndicators(gallery, index);
  };

  const transitionMobileImage = (gallery, image, item, index, itemCount, direction) => {
    if (gallery.dataset.galleryAnimating === 'true') return;

    const previousIndex = Number.parseInt(gallery.dataset.galleryIndex || '0', 10);
    const productLink = image.closest('.custom-bridal-gallery__product-link');
    if (!productLink) return;

    gallery.dataset.galleryAnimating = 'true';
    updateGalleryState(gallery, index, itemCount);

    const incomingImage = image.cloneNode(false);
    incomingImage.classList.remove('motion-reduce', 'custom-bridal-gallery__image');
    incomingImage.classList.add(
      'custom-bridal-gallery__incoming-image',
      direction === 'previous' ? 'is-from-left' : 'is-from-right'
    );
    incomingImage.alt = '';
    incomingImage.setAttribute('aria-hidden', 'true');
    incomingImage.loading = 'eager';
    setImageAttributes(incomingImage, item);
    incomingImage.alt = '';
    productLink.append(incomingImage);

    let completed = false;
    const cleanup = () => {
      delete gallery.dataset.galleryAnimating;
    };
    const fail = () => {
      if (completed) return;
      completed = true;
      incomingImage.remove();
      updateGalleryState(gallery, previousIndex, itemCount);
      cleanup();
    };
    const reveal = () => {
      if (completed) return;
      completed = true;

      void incomingImage.offsetWidth;
      requestAnimationFrame(() => {
        image.classList.add(direction === 'previous' ? 'is-leaving-right' : 'is-leaving-left');
        incomingImage.classList.add('is-active');

        window.setTimeout(() => {
          image.classList.add('is-resetting');
          setImageAttributes(image, item);
          image.classList.remove('is-leaving-left', 'is-leaving-right');
          void image.offsetWidth;
          incomingImage.remove();
          requestAnimationFrame(() => {
            image.classList.remove('is-resetting');
            cleanup();
          });
        }, 260);
      });
    };

    const loadTimeout = window.setTimeout(fail, 8000);
    incomingImage.addEventListener(
      'load',
      () => {
        window.clearTimeout(loadTimeout);
        reveal();
      },
      { once: true }
    );
    incomingImage.addEventListener(
      'error',
      () => {
        window.clearTimeout(loadTimeout);
        fail();
      },
      { once: true }
    );

    if (incomingImage.complete && incomingImage.naturalWidth > 0) {
      window.clearTimeout(loadTimeout);
      reveal();
    }
  };

  const showImageAt = (gallery, nextIndex, requestedDirection) => {
    const image = gallery.querySelector('.custom-bridal-gallery__image');
    const items = getItems(gallery);
    if (!image || items.length < 2) return;

    const currentIndex = Number.parseInt(gallery.dataset.galleryIndex || '0', 10);
    const normalizedIndex = (nextIndex + items.length) % items.length;
    if (normalizedIndex === currentIndex || gallery.dataset.galleryAnimating === 'true') return;

    const nextItem = items[normalizedIndex];
    const direction = requestedDirection || (normalizedIndex < currentIndex ? 'previous' : 'next');
    const shouldAnimate =
      window.matchMedia(mobileGalleryQuery).matches &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (shouldAnimate) {
      transitionMobileImage(gallery, image, nextItem, normalizedIndex, items.length, direction);
      return;
    }

    setImageAttributes(image, nextItem);
    updateGalleryState(gallery, normalizedIndex, items.length);
  };

  const showImage = (gallery, direction) => {
    const items = getItems(gallery);
    if (items.length < 2) return;

    const currentIndex = Number.parseInt(gallery.dataset.galleryIndex || '0', 10);
    const step = direction === 'previous' ? -1 : 1;
    showImageAt(gallery, currentIndex + step, direction);
  };

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const indexedButton = event.target.closest('[data-custom-bridal-gallery-index]');
    if (indexedButton) {
      const gallery = indexedButton.closest('[data-custom-bridal-gallery]');
      if (!gallery) return;

      event.preventDefault();
      event.stopPropagation();
      showImageAt(gallery, Number.parseInt(indexedButton.dataset.customBridalGalleryIndex, 10));
      return;
    }

    const button = event.target.closest('[data-custom-bridal-gallery-direction]');
    if (!button) return;

    const gallery = button.closest('[data-custom-bridal-gallery]');
    if (!gallery) return;

    event.preventDefault();
    event.stopPropagation();
    showImage(gallery, button.dataset.customBridalGalleryDirection);
  });

  document.addEventListener(
    'touchstart',
    (event) => {
      if (!window.matchMedia(mobileGalleryQuery).matches || event.touches.length !== 1) return;
      if (!(event.target instanceof Element)) return;

      const gallery = event.target.closest('[data-custom-bridal-gallery]');
      if (!gallery) return;

      const touch = event.touches[0];
      touchStarts.set(gallery, { x: touch.clientX, y: touch.clientY });
    },
    { passive: true }
  );

  document.addEventListener(
    'touchend',
    (event) => {
      if (!window.matchMedia(mobileGalleryQuery).matches || event.changedTouches.length !== 1) return;
      if (!(event.target instanceof Element)) return;

      const gallery = event.target.closest('[data-custom-bridal-gallery]');
      const start = gallery && touchStarts.get(gallery);
      if (!gallery || !start) return;

      touchStarts.delete(gallery);
      const touch = event.changedTouches[0];
      const deltaX = touch.clientX - start.x;
      const deltaY = touch.clientY - start.y;
      if (Math.abs(deltaX) < 35 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.2) return;

      event.preventDefault();
      event.stopPropagation();
      showImage(gallery, deltaX < 0 ? 'next' : 'previous');
    },
    { passive: false }
  );
})();
